/** Wire → domain. The only place that knows the backend's enumerations. */
import type {
  DeviceCommand,
  DeviceDetail,
  DeviceRuntime,
  DeviceSummary,
  LifecycleEvent,
  Link,
  LiveReading,
  OrderSummary,
  Organisation,
  PhaseReading,
  StockItem,
  WebhookDelivery,
  WebhookHealth,
} from '../../domain/model';
import type { DataState, DeviceLifecycle, OrderState } from '../../domain/status';
import type {
  WireCommand,
  WireDevice,
  WireDeviceStatus,
  WireGap,
  WireGeneration,
  WireInventoryItem,
  WireOrder,
  WireOrderStatus,
  WireTelemetryEvent,
  WireTenant,
  WireWebhookDelivery,
  WireWebhookHealth,
} from '../../wire/internal';

type LifecycleInput = Pick<WireDevice, 'status' | 'activation_state' | 'inventory_state'> & { online: boolean };

export function lifecycleOf(d: LifecycleInput, data: DataState): DeviceLifecycle {
  if (
    d.status === 'retired' ||
    d.status === 'blocked' ||
    d.inventory_state === 'returned' ||
    d.inventory_state === 'retired'
  )
    return 'retired';
  if (d.activation_state === 'activated') {
    if (!d.online) return 'offline';
    return data === 'delayed' ? 'attention' : 'online';
  }
  if (d.inventory_state === 'assigned') return 'assigned';
  if (d.inventory_state === 'shipped') return 'connecting';
  return 'ready';
}

/** Delayed once the latest reading is older than the backend's own online rule: three intervals plus a minute. */
export function dataStateOf(
  latest: WireTelemetryEvent | null | undefined,
  transmitIntervalS: number | null,
  now: Date,
): DataState {
  if (!latest) return 'none';
  const allowed = (3 * Math.max(transmitIntervalS ?? 0, latest.interval_s, 10) + 60) * 1000;
  return now.getTime() - new Date(latest.received_at).getTime() > allowed ? 'delayed' : 'current';
}

const MODELS: Record<string, string> = {
  'p1plug-pi': 'Reference device (Raspberry Pi)',
  'p1plug-sim': 'Virtual device',
};
export const modelOf = (deviceType: string) => MODELS[deviceType] ?? deviceType;
export const isSimulated = (deviceType: string) => deviceType === 'p1plug-sim';

function linkOf(deviceType: string, diagnostics?: Record<string, string> | null): Link {
  // The reference hardware uses the local network. Cellular metrics arrive with the LTE-M hardware.
  if (deviceType === 'p1plug-pi') return { kind: 'lan', hostname: diagnostics?.['pi.hostname'] ?? null };
  return { kind: 'unknown' };
}

export function readingOf(e: WireTelemetryEvent | null | undefined): LiveReading | null {
  if (!e) return null;
  const importW = e.power_import_w ?? 0;
  const exportW = e.power_export_w ?? 0;
  const kwh = (wh: number | null | undefined) => (wh == null ? null : wh / 1000);
  const phases: PhaseReading[] = (e.phases ?? [])
    .filter((p) => p.phase >= 1 && p.phase <= 3)
    .map((p) => ({
      phase: p.phase as 1 | 2 | 3,
      currentA: p.current_ma == null ? null : p.current_ma / 1000,
      voltageV: p.voltage_dv == null ? null : p.voltage_dv / 10,
      estimated: false,
    }));
  return {
    measuredAt: e.event_time,
    receivedAt: e.received_at,
    intervalS: e.interval_s,
    importW,
    exportW,
    netW: importW - exportW,
    tariff: e.tariff === 1 || e.tariff === 2 ? e.tariff : null,
    phases,
    totals: {
      importT1Kwh: kwh(e.energy_import_t1_wh),
      importT2Kwh: kwh(e.energy_import_t2_wh),
      exportT1Kwh: kwh(e.energy_export_t1_wh),
      exportT2Kwh: kwh(e.energy_export_t2_wh),
    },
  };
}

export function deviceSummary(d: WireDevice, now: Date, diagnostics?: Record<string, string> | null): DeviceSummary {
  const data = dataStateOf(d.latest, d.transmit_interval_s, now);
  return {
    id: d.id,
    serial: d.serial_number,
    lifecycle: lifecycleOf(d, data),
    data,
    online: d.online,
    lastSeenAt: d.last_seen_at,
    firmware: d.software_version,
    hardware: d.hardware_revision,
    model: modelOf(d.device_type),
    organisation: d.assignment ? { id: d.assignment.tenant_id, name: d.assignment.tenant_name } : null,
    customerRef: d.assignment?.customer_name ?? null,
    link: linkOf(d.device_type, diagnostics),
    simulated: isSimulated(d.device_type),
  };
}

function runtimeOf(s: WireDeviceStatus): DeviceRuntime | null {
  const r = s.reported;
  if (!r) return null;
  return {
    reportedAt: r.reported_at,
    uptimeS: r.uptime_s,
    resetReason: r.reset_reason,
    bufferRecords: r.buffer_records,
    bufferCapacity: r.buffer_capacity,
    bufferUnacked: r.buffer_unacked,
    retentionSatisfied: r.buffer_retention_satisfied,
    lastError: r.last_error,
    diagnostics: r.diagnostics ?? {},
    meter: {
      id: r.meter?.meter_id || null,
      connected: r.meter?.connected ?? null,
      dsmrVersion: r.meter?.dsmr_version || null,
      telegramsOk: r.meter?.telegrams_ok ?? null,
      telegramsRejected: r.meter?.telegrams_rejected ?? null,
      lastTelegramAt: r.meter?.last_telegram_ms ? new Date(r.meter.last_telegram_ms).toISOString() : null,
    },
  };
}

const ORDER_STATES: Record<WireOrderStatus, OrderState> = {
  requested: 'received',
  awaiting_device: 'ready_for_fulfilment',
  device_assigned: 'processing',
  packed: 'processing',
  shipped: 'shipped',
  delivered: 'delivered',
  activated: 'activated',
  cancelled: 'cancelled',
  replacement_in_progress: 'processing',
};

export function orderSummary(o: WireOrder): OrderSummary {
  return {
    id: o.id,
    reference: o.external_order_id,
    state: ORDER_STATES[o.status] ?? 'exception',
    stage: o.status,
    organisation: { id: o.tenant_id, slug: o.tenant_slug },
    recipient: {
      name: o.delivery.name ?? o.customer_name,
      address: o.delivery.address ?? o.address,
      postalCode: o.delivery.postal_code ?? o.postal_code,
      city: o.delivery.city ?? o.city,
      country: o.delivery.country ?? null,
    },
    customerRef: o.external_customer_id,
    createdAt: o.created_at,
    updatedAt: o.updated_at,
    activatedAt: o.activated_at,
    device: o.device_id && o.device_serial ? { id: o.device_id, serial: o.device_serial } : null,
    shipment: o.shipment
      ? {
          carrier: o.shipment.carrier,
          trackingCode: o.shipment.tracking_code,
          status: o.shipment.status,
          packedAt: o.shipment.packed_at,
          shippedAt: o.shipment.shipped_at,
          deliveredAt: o.shipment.delivered_at,
        }
      : null,
    history: (o.events ?? []).map((e) => ({
      at: e.at,
      from: e.from_status,
      to: e.to_status,
      actor: e.actor,
      note: e.note,
    })),
  };
}

export function webhookHealth(w: WireWebhookHealth): WebhookHealth {
  return {
    configured: w.endpoint !== null,
    url: w.endpoint?.url ?? null,
    enabled: w.endpoint?.enabled ?? false,
    healthy: w.healthy,
    stalled: w.stalled,
    pending: w.pending,
    delivered: w.delivered,
    failed: w.failed,
    lastSuccessAt: w.endpoint?.last_success_at ?? null,
    lastFailureAt: w.endpoint?.last_failure_at ?? null,
    lastError: w.endpoint?.last_error ?? null,
  };
}

export function webhookDelivery(d: WireWebhookDelivery): WebhookDelivery {
  return {
    id: String(d.id),
    at: d.at,
    ok: d.status === 'delivered',
    httpStatus: d.http_status,
    attempt: d.attempt,
    events: d.event_count,
    durationMs: d.duration_ms,
    error: d.error,
  };
}

const KINDS: Record<WireTenant['kind'], Organisation['kind']> = {
  retailer: 'Energy supplier',
  ems: 'Energy-service provider',
  other: 'Other',
};

export function organisation(t: WireTenant): Organisation {
  return {
    id: t.id,
    slug: t.slug,
    name: t.name,
    kind: KINDS[t.kind] ?? 'Other',
    description: t.description,
    since: t.created_at,
    webhook: webhookHealth(t.delivery),
  };
}

export function stockItem(i: WireInventoryItem): StockItem {
  return {
    id: i.id,
    serial: i.serial_number,
    model: modelOf(i.device_type),
    hardware: i.hardware_revision,
    lifecycle: lifecycleOf(
      {
        status: i.status,
        activation_state: i.inventory_state === 'active' ? 'activated' : 'pending',
        inventory_state: i.inventory_state,
        online: true,
      },
      'current',
    ),
    orderId: i.order_id,
    simulated: isSimulated(i.device_type),
  };
}

const ORDER_EVENT_TITLES: Record<string, [LifecycleEvent['kind'], string]> = {
  requested: ['ordered', 'Ordered'],
  device_assigned: ['assigned', 'Assigned to the order'],
  packed: ['packed', 'Packed'],
  shipped: ['shipped', 'Shipped'],
  delivered: ['delivered', 'Delivered'],
  activated: ['activated', 'Activated itself'],
  replacement_in_progress: ['support', 'Replacement started'],
  cancelled: ['support', 'Order cancelled'],
};

/** One chronological story of a device, newest first, from every source the backend has. */
export function lifecycleEvents(
  d: WireDevice,
  order: WireOrder | null,
  generations: WireGeneration[],
  gaps: WireGap[],
  commands: WireCommand[],
): LifecycleEvent[] {
  const events: LifecycleEvent[] = [
    {
      at: d.created_at,
      kind: 'provisioned',
      title: 'Registered with its identity',
      detail: `Serial ${d.serial_number}, certificate issued`,
      actor: 'provisioning',
    },
  ];
  for (const e of order?.events ?? []) {
    const known = ORDER_EVENT_TITLES[e.to_status];
    if (!known) continue;
    events.push({
      at: e.at,
      kind: known[0],
      title: known[1],
      detail: e.note ?? undefined,
      actor: e.actor,
      tone: e.to_status === 'activated' ? 'success' : undefined,
    });
  }
  if (d.first_seen_at)
    events.push({
      at: d.first_seen_at,
      kind: 'first_seen',
      title: 'First connection',
      detail: 'Connected to the platform for the first time',
      actor: 'device',
    });
  for (const g of generations.filter((g) => g.generation_no > 1)) {
    events.push({
      at: g.first_seen_at,
      kind: 'state_reset',
      title: 'Lost its stored state',
      detail: `New sequence generation ${g.generation_no}${g.reset_reason ? ` · reset reason ${g.reset_reason}` : ''} · previous sequence ended at ${g.previous_last_seq}`,
      actor: 'device',
      tone: 'warning',
    });
  }
  for (const g of gaps) {
    events.push({
      at: g.detected_at,
      kind: 'gap',
      title: 'Readings missing',
      detail: `Sequence ${g.from_seq}–${g.to_seq} not received yet`,
      actor: 'ingestion',
      tone: 'warning',
    });
  }
  for (const c of commands) {
    events.push({
      at: c.issued_at,
      kind: 'command',
      title: `Command: ${c.type.replace('_', ' ')}`,
      detail: `${c.status}${c.result_message ? ` · ${c.result_message}` : ''}`,
      actor: 'operator',
      tone: c.status === 'failed' || c.status === 'rejected' ? 'critical' : undefined,
    });
  }
  if (d.online_changed_at && d.online_changed_at !== d.first_seen_at) {
    events.push({
      at: d.online_changed_at,
      kind: 'connectivity',
      title: d.online ? 'Came back online' : 'Went offline',
      actor: 'platform',
      tone: d.online ? 'success' : 'critical',
    });
  }
  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export function deviceCommands(commands: WireCommand[]): DeviceCommand[] {
  return commands.map((c) => ({
    id: c.id,
    type: c.type,
    status: c.status,
    issuedAt: c.issued_at,
    result: c.result_message ?? null,
  }));
}

export function deviceDetail(
  d: WireDevice,
  status: WireDeviceStatus,
  gaps: WireGap[],
  generations: WireGeneration[],
  commands: WireCommand[],
  order: WireOrder | null,
  now: Date,
): DeviceDetail {
  const summary = deviceSummary(d, now, status.reported?.diagnostics);
  const a = d.assignment;
  return {
    ...summary,
    createdAt: d.created_at,
    firstSeenAt: d.first_seen_at,
    onlineThreshold: d.online_threshold,
    transmitIntervalS: d.transmit_interval_s,
    configInSync: d.desired_config_version === d.reported_config_version,
    generationNo: d.generation_no,
    assignment: a
      ? {
          organisation: a.tenant_name,
          customer: a.customer_name,
          address: [a.address, a.city].filter(Boolean).join(', '),
          meterPointId: a.meter_point_id,
          installationNo: a.installation_no,
          orderId: order?.id ?? null,
          orderRef: order?.external_order_id ?? null,
        }
      : null,
    reading: readingOf(d.latest),
    connection: null,
    runtime: runtimeOf(status),
    openGaps: gaps.map((g) => ({ fromSeq: g.from_seq, toSeq: g.to_seq, detectedAt: g.detected_at })),
    commands: deviceCommands(commands),
    events: lifecycleEvents(d, order, generations, gaps, commands),
  };
}
